import { lazy, Suspense, type ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { RequireAuth, RequireRole } from '@/core/auth/RequireRole';
import { useAuth } from '@/core/auth/useAuth';
import {
  AdminShell,
  CounselorShell,
  EmployerShell,
  ParentShell,
  RoyalOverseasShell,
  StudentShell,
  UniversityShell,
} from '@/core/layout/AppLayout';
import { Loader } from '@/core/layout/Loader';
import { LoginPage } from '@/features/auth/pages/LoginPage';
import { ForgotPasswordPage } from '@/features/auth/pages/ForgotPasswordPage';
import { ResetPasswordPage } from '@/features/auth/pages/ResetPasswordPage';
import { roleHomePath } from '@/core/permissions';

const AccountPage = lazy(() =>
  import('@/features/auth/pages/AccountPage').then((m) => ({
    default: m.AccountPage,
  })),
);

const AdminDashboardPage = lazy(() =>
  import('@/features/admin/pages/AdminDashboardPage').then((m) => ({
    default: m.AdminDashboardPage,
  })),
);
const TeacherDashboardPage = lazy(() =>
  import('@/features/teaching/pages/TeacherDashboardPage').then((m) => ({
    default: m.TeacherDashboardPage,
  })),
);
const StudentsListPage = lazy(() =>
  import('@/features/students/pages/StudentsListPage').then((m) => ({
    default: m.StudentsListPage,
  })),
);
const StudentCreatePage = lazy(() =>
  import('@/features/students/pages/StudentCreatePage').then((m) => ({
    default: m.StudentCreatePage,
  })),
);
const StudentImportPage = lazy(() =>
  import('@/features/students/pages/StudentImportPage').then((m) => ({
    default: m.StudentImportPage,
  })),
);
const StudentDetailPage = lazy(() =>
  import('@/features/students/pages/StudentDetailPage').then((m) => ({
    default: m.StudentDetailPage,
  })),
);
const GuardiansListPage = lazy(() =>
  import('@/features/guardians/pages/GuardiansListPage').then((m) => ({
    default: m.GuardiansListPage,
  })),
);
const GuardianImportPage = lazy(() =>
  import('@/features/guardians/pages/GuardianImportPage').then((m) => ({
    default: m.GuardianImportPage,
  })),
);
const MarksEntryPage = lazy(() =>
  import('@/features/academics/pages/MarksEntryPage').then((m) => ({
    default: m.MarksEntryPage,
  })),
);
const ExaminationsListPage = lazy(() =>
  import('@/features/academics/pages/ExaminationsPages').then((m) => ({
    default: m.ExaminationsListPage,
  })),
);
const ExaminationDetailRoute = lazy(() =>
  import('@/features/academics/pages/ExaminationDetailRoute').then((m) => ({
    default: m.ExaminationDetailRoute,
  })),
);
const SubjectsPage = lazy(() =>
  import('@/features/academics/pages/SubjectsPage').then((m) => ({ default: m.SubjectsPage })),
);
const ReportCardsPage = lazy(() =>
  import('@/features/academics/pages/ReportCardsPage').then((m) => ({
    default: m.ReportCardsPage,
  })),
);
const AttendanceCapturePage = lazy(() =>
  import('@/features/attendance/pages/AttendanceCapturePage').then((m) => ({
    default: m.AttendanceCapturePage,
  })),
);
const AttendanceImportPage = lazy(() =>
  import('@/features/attendance/pages/AttendanceImportPage').then((m) => ({
    default: m.AttendanceImportPage,
  })),
);
const AttendanceAlertsPage = lazy(() =>
  import('@/features/attendance/pages/AttendanceAlertsPage').then((m) => ({
    default: m.AttendanceAlertsPage,
  })),
);
const NotificationsListPage = lazy(() =>
  import('@/features/notifications/pages/NotificationsListPage').then((m) => ({
    default: m.NotificationsListPage,
  })),
);
const SendNotificationPage = lazy(() =>
  import('@/features/notifications/pages/SendNotificationPage').then((m) => ({
    default: m.SendNotificationPage,
  })),
);
const ReportsListPage = lazy(() =>
  import('@/features/reports/pages/ReportsListPage').then((m) => ({
    default: m.ReportsListPage,
  })),
);
const UsersPage = lazy(() =>
  import('@/features/admin/pages/UsersPage').then((m) => ({ default: m.UsersPage })),
);
const UserDetailPage = lazy(() =>
  import('@/features/admin/pages/UserDetailPage').then((m) => ({ default: m.UserDetailPage })),
);
const RolesPermissionsPage = lazy(() =>
  import('@/features/admin/pages/RolesPermissionsPage').then((m) => ({
    default: m.RolesPermissionsPage,
  })),
);
const SchoolSettingsPage = lazy(() =>
  import('@/features/admin/pages/SchoolSettingsPage').then((m) => ({
    default: m.SchoolSettingsPage,
  })),
);
const SettingsOverviewPage = lazy(() =>
  import('@/features/admin/pages/SettingsOverviewPage').then((m) => ({
    default: m.SettingsOverviewPage,
  })),
);
const GradingSystemsPage = lazy(() =>
  import('@/features/admin/pages/GradingSystemsPage').then((m) => ({
    default: m.GradingSystemsPage,
  })),
);
const AttendanceRulesPage = lazy(() =>
  import('@/features/admin/pages/AttendanceRulesPage').then((m) => ({
    default: m.AttendanceRulesPage,
  })),
);
const SchoolCalendarPage = lazy(() =>
  import('@/features/admin/pages/SchoolCalendarPage').then((m) => ({
    default: m.SchoolCalendarPage,
  })),
);
const NotificationDefaultsPage = lazy(() =>
  import('@/features/admin/pages/NotificationDefaultsPage').then((m) => ({
    default: m.NotificationDefaultsPage,
  })),
);
const NotificationTemplatesPage = lazy(() =>
  import('@/features/admin/pages/NotificationTemplatesPage').then((m) => ({
    default: m.NotificationTemplatesPage,
  })),
);
const EducationLevelsPage = lazy(() =>
  import('@/features/admin/pages/EducationLevelsPage').then((m) => ({
    default: m.EducationLevelsPage,
  })),
);

const ParentHomePage = lazy(() =>
  import('@/features/admin/pages/ParentHomePage').then((m) => ({ default: m.ParentHomePage })),
);
const ParentProgressPage = lazy(() =>
  import('@/features/admin/pages/ParentProgressPage').then((m) => ({
    default: m.ParentProgressPage,
  })),
);
const ParentReportsPage = lazy(() =>
  import('@/features/admin/pages/ParentReportsPage').then((m) => ({
    default: m.ParentReportsPage,
  })),
);
const ParentAlertsPage = lazy(() =>
  import('@/features/admin/pages/ParentAlertsPage').then((m) => ({
    default: m.ParentAlertsPage,
  })),
);
const ParentCareerPage = lazy(() =>
  import('@/features/admin/pages/ParentCareerPage').then((m) => ({
    default: m.ParentCareerPage,
  })),
);
const StudentHomePage = lazy(() =>
  import('@/features/students/pages/StudentPortalPages').then((m) => ({
    default: m.StudentHomePage,
  })),
);
const StudentAcademicsPage = lazy(() =>
  import('@/features/students/pages/StudentPortalPages').then((m) => ({
    default: m.StudentAcademicsPage,
  })),
);
const StudentAcademicsClassPage = lazy(() =>
  import('@/features/students/pages/StudentPortalPages').then((m) => ({
    default: m.StudentAcademicsClassPage,
  })),
);
const StudentCareerPage = lazy(() =>
  import('@/features/students/pages/StudentPortalPages').then((m) => ({
    default: m.StudentCareerPage,
  })),
);
const StudentScholarshipsPage = lazy(() =>
  import('@/features/students/pages/StudentPortalPages').then((m) => ({
    default: m.StudentScholarshipsPage,
  })),
);
const StudentJobsPage = lazy(() =>
  import('@/features/students/pages/StudentPortalPages').then((m) => ({
    default: m.StudentJobsPage,
  })),
);
const StudentJobBrowsePage = lazy(() =>
  import('@/features/students/pages/StudentPortalPages').then((m) => ({
    default: m.StudentJobBrowsePage,
  })),
);
const StudentJobApplicationsPage = lazy(() =>
  import('@/features/students/pages/StudentPortalPages').then((m) => ({
    default: m.StudentJobApplicationsPage,
  })),
);
const StudentJobProfilePage = lazy(() =>
  import('@/features/jobs/components/StudentJobProfilePage').then((m) => ({
    default: m.StudentJobProfilePage,
  })),
);
const StudentAlertsPage = lazy(() =>
  import('@/features/students/pages/StudentPortalPages').then((m) => ({
    default: m.StudentAlertsPage,
  })),
);
const StudentMorePage = lazy(() =>
  import('@/features/students/pages/StudentPortalPages').then((m) => ({
    default: m.StudentMorePage,
  })),
);

const CounselorHomePage = lazy(() =>
  import('@/features/career/pages/CounselorHomePage').then((m) => ({
    default: m.CounselorHomePage,
  })),
);
const AssessmentsPage = lazy(() =>
  import('@/features/career/pages/AssessmentsPage').then((m) => ({
    default: m.AssessmentsPage,
  })),
);
const RecommendationsPage = lazy(() =>
  import('@/features/career/pages/RecommendationsPage').then((m) => ({
    default: m.RecommendationsPage,
  })),
);
const CareerPlansPage = lazy(() =>
  import('@/features/career/pages/CareerPlansPage').then((m) => ({
    default: m.CareerPlansPage,
  })),
);
const SchedulingPage = lazy(() =>
  import('@/features/career/pages/SchedulingPage').then((m) => ({
    default: m.SchedulingPage,
  })),
);
const CombinationsPage = lazy(() =>
  import('@/features/university/pages/CombinationsPage').then((m) => ({
    default: m.CombinationsPage,
  })),
);
const GapsPage = lazy(() =>
  import('@/features/university/pages/GapsPage').then((m) => ({ default: m.GapsPage })),
);
const ScholarshipsBrowsePage = lazy(() =>
  import('@/features/scholarships/pages/ScholarshipsBrowsePage').then((m) => ({
    default: m.ScholarshipsBrowsePage,
  })),
);
const ScholarshipMatchesPage = lazy(() =>
  import('@/features/scholarships/pages/ScholarshipMatchesPage').then((m) => ({
    default: m.ScholarshipMatchesPage,
  })),
);
const CounselorSubmitAdmissionPage = lazy(() =>
  import('@/features/admissions/pages/AdmissionsPages').then((m) => ({
    default: m.CounselorSubmitAdmissionPage,
  })),
);
const VisasPage = lazy(() =>
  import('@/features/career/pages/VisasPage').then((m) => ({
    default: m.VisasPage,
  })),
);
const UniversityDashboardPage = lazy(() =>
  import('@/features/university/pages/UniversityPortalPages').then((m) => ({
    default: m.UniversityDashboardPage,
  })),
);
const UniversityApplicationsPage = lazy(() =>
  import('@/features/university/pages/UniversityPortalPages').then((m) => ({
    default: m.UniversityApplicationsPage,
  })),
);
const UniversityProgramsPage = lazy(() =>
  import('@/features/university/pages/UniversityPortalPages').then((m) => ({
    default: m.UniversityProgramsPage,
  })),
);
const CounselorJobsBrowsePage = lazy(() =>
  import('@/features/jobs/pages/JobsPages').then((m) => ({
    default: m.CounselorJobsBrowsePage,
  })),
);
const CounselorJobApplicationsPage = lazy(() =>
  import('@/features/jobs/pages/JobsPages').then((m) => ({
    default: m.CounselorJobApplicationsPage,
  })),
);
const EmployerDashboardPage = lazy(() =>
  import('@/features/jobs/pages/EmployerPortalPages').then((m) => ({
    default: m.EmployerDashboardPage,
  })),
);
const EmployerPostingsPage = lazy(() =>
  import('@/features/jobs/pages/EmployerPortalPages').then((m) => ({
    default: m.EmployerPostingsPage,
  })),
);
const EmployerApplicationsPage = lazy(() =>
  import('@/features/jobs/pages/EmployerPortalPages').then((m) => ({
    default: m.EmployerApplicationsPage,
  })),
);
const CareerReportsPage = lazy(() =>
  import('@/features/reports/pages/CareerReportsPage').then((m) => ({
    default: m.CareerReportsPage,
  })),
);
const RiskReportsPage = lazy(() =>
  import('@/features/reports/pages/RiskReportsPage').then((m) => ({
    default: m.RiskReportsPage,
  })),
);

const RoyalOverseasDashboardPage = lazy(() =>
  import('@/features/admin/pages/RoyalOverseasDashboardPage').then((m) => ({
    default: m.RoyalOverseasDashboardPage,
  })),
);
const SchoolsListPage = lazy(() =>
  import('@/features/admin/pages/SchoolsListPage').then((m) => ({
    default: m.SchoolsListPage,
  })),
);
const SchoolDetailPage = lazy(() =>
  import('@/features/admin/pages/SchoolDetailPage').then((m) => ({
    default: m.SchoolDetailPage,
  })),
);
const RevenuePage = lazy(() =>
  import('@/features/admin/pages/RevenuePage').then((m) => ({ default: m.RevenuePage })),
);
const AssessmentsOverviewPage = lazy(() =>
  import('@/features/admin/pages/AssessmentsOverviewPage').then((m) => ({
    default: m.AssessmentsOverviewPage,
  })),
);
const SystemUsagePage = lazy(() =>
  import('@/features/admin/pages/SystemUsagePage').then((m) => ({
    default: m.SystemUsagePage,
  })),
);
const LocationsPage = lazy(() =>
  import('@/features/admin/pages/LocationsPage').then((m) => ({
    default: m.LocationsPage,
  })),
);
const JobCategoriesPage = lazy(() =>
  import('@/features/admin/pages/JobCategoriesPage').then((m) => ({
    default: m.JobCategoriesPage,
  })),
);
const CourseCategoriesPage = lazy(() =>
  import('@/features/admin/pages/CourseCategoriesPage').then((m) => ({
    default: m.CourseCategoriesPage,
  })),
);
const CourseLevelsPage = lazy(() =>
  import('@/features/admin/pages/CourseLevelsPage').then((m) => ({
    default: m.CourseLevelsPage,
  })),
);
const CatalogCoursesPage = lazy(() =>
  import('@/features/admin/pages/CatalogCoursesPage').then((m) => ({
    default: m.CatalogCoursesPage,
  })),
);
const UniversitiesPage = lazy(() =>
  import('@/features/admin/pages/UniversitiesPage').then((m) => ({
    default: m.UniversitiesPage,
  })),
);
const UniversityDetailPage = lazy(() =>
  import('@/features/admin/pages/UniversityDetailPage').then((m) => ({
    default: m.UniversityDetailPage,
  })),
);
const ScholarshipsAdminPage = lazy(() =>
  import('@/features/admin/pages/ScholarshipsAdminPage').then((m) => ({
    default: m.ScholarshipsAdminPage,
  })),
);
const CurrenciesPage = lazy(() =>
  import('@/features/admin/pages/CurrenciesPage').then((m) => ({
    default: m.CurrenciesPage,
  })),
);
const CountriesPage = lazy(() =>
  import('@/features/admin/pages/CountriesPage').then((m) => ({
    default: m.CountriesPage,
  })),
);
const JobsMatchingSettingsPage = lazy(() =>
  import('@/features/admin/pages/JobsMatchingSettingsPage').then((m) => ({
    default: m.JobsMatchingSettingsPage,
  })),
);
const SystemSettingsPage = lazy(() =>
  import('@/features/admin/pages/SystemSettingsPage').then((m) => ({
    default: m.SystemSettingsPage,
  })),
);
const FeatureFlagsPage = lazy(() =>
  import('@/features/admin/pages/FeatureFlagsPage').then((m) => ({
    default: m.FeatureFlagsPage,
  })),
);
const AgenciesPage = lazy(() =>
  import('@/features/admin/pages/AgenciesPage').then((m) => ({
    default: m.AgenciesPage,
  })),
);
const AgencyDetailPage = lazy(() =>
  import('@/features/admin/pages/AgencyDetailPage').then((m) => ({
    default: m.AgencyDetailPage,
  })),
);
const CommissionsPage = lazy(() =>
  import('@/features/admin/pages/CommissionsPage').then((m) => ({
    default: m.CommissionsPage,
  })),
);
const PlansPage = lazy(() =>
  import('@/features/admin/pages/PlansPage').then((m) => ({
    default: m.PlansPage,
  })),
);

function HomeRedirect() {
  const { isAuthenticated, role } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <Navigate to={roleHomePath(role)} replace />;
}

function DashboardIndexRoute() {
  const { role } = useAuth();
  if (role === 'teacher') {
    return (
      <Lazy>
        <TeacherDashboardPage />
      </Lazy>
    );
  }
  return (
    <Lazy>
      <AdminDashboardPage />
    </Lazy>
  );
}

function NotFoundPage() {
  const { role, isAuthenticated } = useAuth();
  return (
    <Navigate to={isAuthenticated ? roleHomePath(role) : '/login'} replace />
  );
}

function Lazy({ children }: { children: ReactNode }) {
  return <Suspense fallback={<Loader />}>{children}</Suspense>;
}

export function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      <Route element={<RequireAuth />}>
        <Route path="/" element={<HomeRedirect />} />

        <Route element={<AdminShell />}>
          <Route
            path="/account"
            element={
              <Lazy>
                <AccountPage />
              </Lazy>
            }
          />
        </Route>

        <Route element={<RequireRole roles={['school_admin', 'teacher']} />}>
          <Route path="/admin" element={<AdminShell />}>
            <Route index element={<DashboardIndexRoute />} />
            <Route path="students" element={<Lazy><StudentsListPage /></Lazy>} />
            <Route element={<RequireRole roles={['school_admin']} />}>
              <Route path="students/new" element={<Lazy><StudentCreatePage /></Lazy>} />
              <Route path="students/import" element={<Lazy><StudentImportPage /></Lazy>} />
              <Route path="guardians/import" element={<Lazy><GuardianImportPage /></Lazy>} />
            </Route>
            <Route path="students/:studentId" element={<Lazy><StudentDetailPage /></Lazy>} />
            <Route path="guardians" element={<Lazy><GuardiansListPage /></Lazy>} />
            <Route path="academics/examinations" element={<Lazy><ExaminationsListPage /></Lazy>} />
            <Route path="academics/examinations/:examinationId" element={<Lazy><ExaminationDetailRoute /></Lazy>} />
            <Route path="academics/marks" element={<Lazy><MarksEntryPage /></Lazy>} />
            <Route path="academics/subjects" element={<Lazy><SubjectsPage /></Lazy>} />
            <Route path="academics/report-cards" element={<Lazy><ReportCardsPage /></Lazy>} />
            <Route path="attendance" element={<Lazy><AttendanceCapturePage /></Lazy>} />
            <Route path="attendance/import" element={<Lazy><AttendanceImportPage /></Lazy>} />
            <Route path="attendance/alerts" element={<Lazy><AttendanceAlertsPage /></Lazy>} />
            <Route path="notifications" element={<Lazy><NotificationsListPage /></Lazy>} />
            <Route path="notifications/send" element={<Lazy><SendNotificationPage /></Lazy>} />
            <Route path="reports" element={<Lazy><ReportsListPage /></Lazy>} />
            <Route path="settings" element={<Lazy><SettingsOverviewPage /></Lazy>} />
            <Route path="settings/general" element={<Lazy><SchoolSettingsPage /></Lazy>} />
            <Route element={<RequireRole roles={['school_admin']} />}>
              <Route path="settings/levels" element={<Lazy><EducationLevelsPage /></Lazy>} />
              <Route path="settings/grading" element={<Lazy><GradingSystemsPage /></Lazy>} />
              <Route path="settings/calendar" element={<Lazy><SchoolCalendarPage /></Lazy>} />
              <Route path="settings/attendance" element={<Lazy><AttendanceRulesPage /></Lazy>} />
              <Route path="settings/notifications" element={<Lazy><NotificationDefaultsPage /></Lazy>} />
              <Route path="settings/notification-templates" element={<Lazy><NotificationTemplatesPage /></Lazy>} />
              <Route path="users" element={<Lazy><UsersPage /></Lazy>} />
              <Route path="users/roles" element={<Lazy><RolesPermissionsPage /></Lazy>} />
              <Route path="users/:userId" element={<Lazy><UserDetailPage /></Lazy>} />
            </Route>
          </Route>
        </Route>

        <Route element={<RequireRole roles={['parent']} />}>
          <Route path="/parent" element={<ParentShell />}>
            <Route index element={<Lazy><ParentHomePage /></Lazy>} />
            <Route path="progress" element={<Lazy><ParentProgressPage /></Lazy>} />
            <Route path="attendance" element={<Navigate to="/parent/progress" replace />} />
            <Route path="reports" element={<Lazy><ParentReportsPage /></Lazy>} />
            <Route path="alerts" element={<Lazy><ParentAlertsPage /></Lazy>} />
            <Route path="career" element={<Lazy><ParentCareerPage /></Lazy>} />
          </Route>
        </Route>

        <Route element={<RequireRole roles={['student']} />}>
          <Route path="/student" element={<StudentShell />}>
            <Route index element={<Lazy><StudentHomePage /></Lazy>} />
            <Route path="academics" element={<Lazy><StudentAcademicsPage /></Lazy>} />
            <Route
              path="academics/class/:classKey"
              element={<Lazy><StudentAcademicsClassPage /></Lazy>}
            />
            <Route path="career" element={<Lazy><StudentCareerPage /></Lazy>} />
            <Route path="scholarships" element={<Lazy><StudentScholarshipsPage /></Lazy>} />
            <Route path="jobs" element={<Lazy><StudentJobsPage /></Lazy>} />
            <Route
              path="jobs/browse/:categoryId"
              element={<Lazy><StudentJobBrowsePage /></Lazy>}
            />
            <Route
              path="jobs/applications"
              element={<Lazy><StudentJobApplicationsPage /></Lazy>}
            />
            <Route
              path="jobs/profile"
              element={<Lazy><StudentJobProfilePage /></Lazy>}
            />
            <Route
              path="jobs/profile/:section"
              element={<Lazy><StudentJobProfilePage /></Lazy>}
            />
            <Route path="alerts" element={<Lazy><StudentAlertsPage /></Lazy>} />
            <Route path="more" element={<Lazy><StudentMorePage /></Lazy>} />
            <Route path="account" element={<Lazy><AccountPage /></Lazy>} />
          </Route>
        </Route>

        <Route element={<RequireRole roles={['counselor']} />}>
          <Route path="/counselor" element={<CounselorShell />}>
            <Route index element={<Lazy><CounselorHomePage /></Lazy>} />
            <Route path="students" element={<Lazy><StudentsListPage /></Lazy>} />
            <Route path="students/:studentId" element={<Lazy><StudentDetailPage /></Lazy>} />
            <Route path="assessments" element={<Lazy><AssessmentsPage /></Lazy>} />
            <Route path="recommendations" element={<Lazy><RecommendationsPage /></Lazy>} />
            <Route path="plans" element={<Lazy><CareerPlansPage /></Lazy>} />
            <Route path="scheduling" element={<Lazy><SchedulingPage /></Lazy>} />
            <Route path="university" element={<Lazy><CombinationsPage /></Lazy>} />
            <Route path="university/gaps" element={<Lazy><GapsPage /></Lazy>} />
            <Route path="university/admissions" element={<Lazy><CounselorSubmitAdmissionPage /></Lazy>} />
            <Route path="university/visas" element={<Lazy><VisasPage /></Lazy>} />
            <Route path="scholarships" element={<Lazy><ScholarshipsBrowsePage /></Lazy>} />
            <Route path="scholarships/matches" element={<Lazy><ScholarshipMatchesPage /></Lazy>} />
            <Route path="jobs" element={<Lazy><CounselorJobsBrowsePage /></Lazy>} />
            <Route path="jobs/applications" element={<Lazy><CounselorJobApplicationsPage /></Lazy>} />
            <Route path="reports" element={<Lazy><CareerReportsPage /></Lazy>} />
            <Route path="reports/risk" element={<Lazy><RiskReportsPage /></Lazy>} />
          </Route>
        </Route>

        <Route element={<RequireRole roles={['university']} />}>
          <Route path="/university" element={<UniversityShell />}>
            <Route index element={<Lazy><UniversityDashboardPage /></Lazy>} />
            <Route path="admissions" element={<Lazy><UniversityApplicationsPage /></Lazy>} />
            <Route path="courses" element={<Lazy><UniversityProgramsPage /></Lazy>} />
            <Route path="programs" element={<Lazy><UniversityProgramsPage /></Lazy>} />
          </Route>
        </Route>

        <Route element={<RequireRole roles={['employer']} />}>
          <Route path="/employer" element={<EmployerShell />}>
            <Route index element={<Lazy><EmployerDashboardPage /></Lazy>} />
            <Route path="postings" element={<Lazy><EmployerPostingsPage /></Lazy>} />
            <Route path="applications" element={<Lazy><EmployerApplicationsPage /></Lazy>} />
          </Route>
        </Route>

        <Route element={<RequireRole roles={['super_admin']} />}>
          <Route path="/royal-overseas" element={<RoyalOverseasShell />}>
            <Route index element={<Lazy><RoyalOverseasDashboardPage /></Lazy>} />
            <Route path="schools" element={<Lazy><SchoolsListPage /></Lazy>} />
            <Route
              path="schools/new"
              element={<Navigate to="/royal-overseas/schools?onboard=1" replace />}
            />
            <Route path="schools/:schoolId" element={<Lazy><SchoolDetailPage /></Lazy>} />
            <Route path="users" element={<Lazy><UsersPage /></Lazy>} />
            <Route path="users/roles" element={<Lazy><RolesPermissionsPage /></Lazy>} />
            <Route path="users/:userId" element={<Lazy><UserDetailPage /></Lazy>} />
            <Route path="revenue" element={<Lazy><RevenuePage /></Lazy>} />
            <Route path="assessments" element={<Lazy><AssessmentsOverviewPage /></Lazy>} />
            <Route path="usage" element={<Lazy><SystemUsagePage /></Lazy>} />
            <Route path="locations" element={<Lazy><LocationsPage /></Lazy>} />
            <Route path="currencies" element={<Lazy><CurrenciesPage /></Lazy>} />
            <Route path="countries" element={<Lazy><CountriesPage /></Lazy>} />
            <Route path="job-categories" element={<Lazy><JobCategoriesPage /></Lazy>} />
            <Route path="universities" element={<Lazy><UniversitiesPage /></Lazy>} />
            <Route path="universities/:universityId" element={<Lazy><UniversityDetailPage /></Lazy>} />
            <Route path="agencies" element={<Lazy><AgenciesPage /></Lazy>} />
            <Route path="agencies/:agencyId" element={<Lazy><AgencyDetailPage /></Lazy>} />
            <Route path="scholarships" element={<Lazy><ScholarshipsAdminPage /></Lazy>} />
            <Route path="commissions" element={<Lazy><CommissionsPage /></Lazy>} />
            <Route path="plans" element={<Lazy><PlansPage /></Lazy>} />
            <Route path="feature-flags" element={<Lazy><FeatureFlagsPage /></Lazy>} />
            <Route path="course-categories" element={<Lazy><CourseCategoriesPage /></Lazy>} />
            <Route path="course-levels" element={<Lazy><CourseLevelsPage /></Lazy>} />
            <Route path="courses" element={<Lazy><CatalogCoursesPage /></Lazy>} />
            <Route path="jobs-matching" element={<Lazy><JobsMatchingSettingsPage /></Lazy>} />
            <Route path="settings" element={<Lazy><SystemSettingsPage /></Lazy>} />
          </Route>
        </Route>
      </Route>

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
